import React, {useEffect, useState} from "react";
import 'react-toastify/dist/ReactToastify.css';
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import {useLocation} from "react-router-dom";
import {toast} from "react-toastify";
import {history} from "../../redux/store/configureStore";
import ClientForm from "./ClientForm";
import ClientModalDelete from "./ClientModalDelete";
import ClientDuplicateModal from "./ClientDuplicateModal";
import ClientMergeDialog from "./ClientMergeDialog";
import {STATUS} from "../common/Const";
import {validatePerson} from "../common/Validations";
import StudentsList from "./StudentsList";
import * as clientActions from "../../redux/actions/clientActions";
import * as studentActions from "../../redux/actions/studentActions";

import {getById, getStudentsByClientId, hasErrors, hasNoErrors} from "../common/Helpers";
import DuplicateWarning from "../common/DuplicateWarning";
import {
  buildDefaultNewClientNotifications,
  normalizeLessonRemindersForForm,
  normalizePaymentRemindersForForm,
  updateLessonReminderPreference,
  updatePaymentReminderPreference,
} from "./clientNotificationPreferences";
import {
  buildManageRouteWithSchoolYear,
  buildSearchWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";

const Client = ({clients, loadClients, students, studentsOfClient, loadStudents, saveClient, archiveClient, deleteClient, mergeClients, ...props}) => {
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [client, setClient] = useState({ ...props.client });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(STATUS.VIEW);
  const [showInvoiceData, setShowInvoiceData] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateClients, setDuplicateClients] = useState([]);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);

  useEffect(() => {
    if (clients.length === 0) {
      loadClients().catch((error) => {
        alert(`Loading clients failed${error}`);
      });
    }
  }, []);

  useEffect(() => {
    setClient({...props.client});
    if (props.client.id) {
      setStatus(STATUS.VIEW);
    } else {
      setStatus(STATUS.IDLE);
    }
    setShowInvoiceData(!!props.client.invoiceData);
  }, [props.client]);

  useEffect(() => {
    if (students.length === 0) {
      loadStudents().catch((error) => {
        alert(`Loading students failed${error}`);
      });
    }
  }, []);

  useEffect(() => {
    if (client && clients.length > 0) {
      const { checkForDuplicateClient } = require('../common/Validations');
      const duplicates = checkForDuplicateClient(client, clients, client.id);
      setDuplicateClients(duplicates);
    }
  }, [client, clients]);

  // Handle URL query param for opening merge dialog (e.g., after saving new client)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mergeWithId = params.get('mergeWith');

    if (mergeWithId && clients.length > 0 && client.id) {
      const duplicateClient = clients.find(c => c.id === mergeWithId);
      if (duplicateClient && client.id !== mergeWithId) {
        setMergeTarget(duplicateClient);
        setShowMergeDialog(true);
        // Clean URL to remove query param
        history.replace({
          pathname: `/manage/client/${client.id}`,
          search: buildSearchWithSchoolYear("", activeSchoolYearStartYear),
        });
      }
    }
  }, [location.search, clients, client.id]);

  const validate = () => {
    // eslint-disable-next-line no-shadow
    const errors = validatePerson(client);

    setErrors(errors);
    return hasNoErrors(errors);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched({ ...touched, [name]: true });
    validate();
  };

  const handleCancel = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/clients", location.search, activeSchoolYearStartYear));
  };

  const handleArchive = () => {
    archiveClient(client);
    setFormDisabled(!formDisabled);
  };

  const handleDelete = () => {
    deleteClient(client);
    history.goBack();
  };

  const handleShowInvoiceClick = () => {
    setShowInvoiceData(true);
  };

  const handleNotificationHistoryClick = () => {
    history.push(buildManageRouteWithSchoolYear(`/manage/client/${client.id}/notifications`, location.search, activeSchoolYearStartYear));
  };

  const handleEditStudent = (student) => {
    history.push(buildManageRouteWithSchoolYear(`/manage/student/${student.id}`, location.search, activeSchoolYearStartYear));
  };

  const handleAddStudent = (client) => {
    history.push(buildManageRouteWithSchoolYear("/manage/student/new", `?clientId=${client.id}`, activeSchoolYearStartYear));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let updatedClient = { ...client, [name]: value };
    if (name.startsWith("lessonReminders.")) {
      updatedClient = updateLessonReminderPreference(client, name.split(".")[1], value);
    } else if (name.startsWith("paymentReminders.")) {
      updatedClient = updatePaymentReminderPreference(client, name.split(".")[1], value);
    }

    setClient(updatedClient);
    setTouched({ ...touched, [name]: true });
    validate();
  };

  const handleConfirmFromModal = () => {
    setShowDuplicateModal(false);
    // Proceed with save - duplicate check already done
    saveClient(client)
      .then((savedClient) => {
        setStatus(STATUS.VIEW);
        setTouched({});
        if (!client.id) {
          history.push(buildManageRouteWithSchoolYear(`/manage/client/${savedClient.id}`, location.search, activeSchoolYearStartYear));
        }
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handleMergeFromModal = (duplicateClient) => {
    setShowDuplicateModal(false);

    // If creating new client, we need to save it first before merging
    if (!client.id) {
      // Save the new client first, then open merge for the saved client
      saveClient(client)
        .then((savedClient) => {
          setClient(savedClient);
          history.push(buildManageRouteWithSchoolYear(`/manage/client/${savedClient.id}`, `?mergeWith=${duplicateClient.id}`, activeSchoolYearStartYear));
        })
        .catch((error) => {
          toast.error(`Nie można zapisać klienta: ${error.message}`);
        });
    } else {
      handleOpenMergeDialog(duplicateClient);
    }
  };

  const handleOpenMergeDialog = (duplicateClient) => {
    setMergeTarget(duplicateClient);
    setShowMergeDialog(true);
  };

  const handleConfirmMerge = async (mergeData) => {
    try {
      await mergeClients(client.id, mergeTarget.id, mergeData);
      toast.success('Klienci zostali pomyślnie scaleni');
      setShowMergeDialog(false);
    } catch (error) {
      throw error; // Let dialog handle error display
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(STATUS.SUBMITTING);
    const isValid = validate();

    if (isValid) {
      // Check for duplicates before saving
      if (duplicateClients.length > 0) {
        setShowDuplicateModal(true);
        return;
      }

      // No duplicates - proceed with save
      saveClient(client)
        .then((savedClient) => {
          setStatus(STATUS.VIEW);
          setTouched({});
          if (!client.id) {
            history.push(buildManageRouteWithSchoolYear(`/manage/client/${savedClient.id}`, location.search, activeSchoolYearStartYear));
          }
        })
        .catch((error) => {
          setErrors({...errors, saveError: error.message});
          setStatus(STATUS.SUBMITTED);
        });
    } else {
      setStatus(STATUS.SUBMITTED);
    }
  };

  const disabled = status === STATUS.SUBMITTING;
  const isModified = Object.keys(touched).filter(value => value).length > 0;
  return (
    <div className="mb-5">
      { hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).map(key => <div key={key}>{errors[key]}</div>)}
        </div>
      )}
      <div className="row m-0 p-0">
        <div className="col-12 p-0">
          <ClientForm
            firstName={client.firstName}
            lastName={client.lastName}
            email={client.email}
            mobile={client.mobile}
            dataProcessingAgreement={client.dataProcessingAgreement}
            sendNotifications={client.sendNotifications}
            lessonReminders={normalizeLessonRemindersForForm(client.lessonReminders, { defaultEnabled: !client.id })}
            paymentReminders={normalizePaymentRemindersForForm(client.paymentReminders)}
            hasContract={client.hasContract}
            invoiceData={client.invoiceData}
            address={client.address}
            contractDate={client.contractDate}
            notes={client.notes}
            touched={touched}
            errors={errors}
            onBlur={handleBlur}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onShowInvoiceClick={handleShowInvoiceClick}
            onNotificationHistoryClick={client.id ? handleNotificationHistoryClick : null}
            status={status}
            disabled={disabled}
            showInvoiceData={showInvoiceData}
            archived={client.archived}
          />
        </div>
        <div className="col-lg-6 p-0" />
      </div>

      {client.id && (
        <React.Fragment>
          <div className="pt-3 row content">
            <div className="col-md-12" >
              <StudentsList students={studentsOfClient} onViewParent={() => {}} onEdit={handleEditStudent} />
            </div>
          </div>
        </React.Fragment>
      )}

      <div className="row content mt-3">
        {isModified && !client.archived && (
          <div className="col-md-block mb-2" >
            <button type="button" className="btn manage-button manage-red-button ml-2 " disabled={disabled} onClick={handleSubmit}>Zapisz</button>
          </div>
        )}
        {!isModified && client.id && !client.archived && (
          <div className="col-md-block mb-2">
            <button type="button" className="btn manage-button manage-red-button ml-2" onClick={() => handleAddStudent(client)}>Dodaj nowego ucznia</button>
          </div>
        )}
        {client.id && !isModified && (
          <div className="col-md-block mb-2">
            <button
              type="button"
              className="btn manage-button btn-dark ml-2"
              onClick={() => history.push(buildManageRouteWithSchoolYear(`/manage/client/${client.id}/ledger`, location.search, activeSchoolYearStartYear))}
            >
              Rozliczenia
            </button>
          </div>
        )}
        <div className="col-md-block mr-auto pl-2 mb-2" >
          <button type="button" className="btn manage-button manage-teal-button" onClick={handleCancel}>Powrót</button>
        </div>
        {
          client.archived
            ? (
              <div className="col-md-block pr-2 mb-2">
                <button type="button" className="btn manage-button manage-red-button ml-2" onClick={() => setModalShow(true)}>Usuń</button>
                <button type="button" className="btn manage-button btn-success ml-2" onClick={handleArchive}>Przywróć</button>
                <ClientModalDelete
                  show={modalShow}
                  onHide={() => setModalShow(false)}
                  handleClick={() => handleDelete()}
                />
              </div>
            )
            : <button type="button" className="btn manage-button btn-success ml-2 mr-2 mb-2" onClick={handleArchive}>Zarchiwizuj</button>
        }
      </div>
      {duplicateClients.length > 0 && (
        <DuplicateWarning
          duplicates={duplicateClients}
          onNavigate={(clientId) => history.push(buildManageRouteWithSchoolYear(`/manage/client/${clientId}`, location.search, activeSchoolYearStartYear))}
          onMerge={handleMergeFromModal}
        />
      )}
      <ClientDuplicateModal
        show={showDuplicateModal}
        duplicates={duplicateClients}
        onConfirm={handleConfirmFromModal}
        onMerge={handleMergeFromModal}
        onClose={() => setShowDuplicateModal(false)}
      />
      <ClientMergeDialog
        show={showMergeDialog}
        sourceClient={client}
        targetClient={mergeTarget}
        studentsA={studentsOfClient}
        studentsB={students.filter(s => mergeTarget && s.clientId === mergeTarget.id)}
        onConfirm={handleConfirmMerge}
        onClose={() => setShowMergeDialog(false)}
      />
    </div>
  );
};

Client.propTypes = {
  clients: PropTypes.array.isRequired,
  client: PropTypes.object.isRequired,
  studentsOfClient: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  saveClient: PropTypes.func.isRequired,
  students: PropTypes.array.isRequired,
  loadStudents: PropTypes.func.isRequired,
  mergeClients: PropTypes.func.isRequired,
};

const newClient = buildDefaultNewClientNotifications();

const mapStateToProps = (state, ownProps) => {
  const {id} = ownProps.match.params;
  let client =
    id === "new" || state.clients.length === 0
      ? newClient
      : getById(state.clients, id);
  if (!client) {
    client = newClient;
  }
  const studentsOfClient =
      !client.id || state.students.length === 0
        ? []
        : getStudentsByClientId(state.students, id, client);
  return {
    client,
    clients: state.clients,
    students: state.students,
    studentsOfClient,
  };
};

const mapDispatchToProps = dispatch => ({
  loadClients: () => dispatch(clientActions.loadClients()),
  saveClient: client => dispatch(clientActions.saveClient(client)),
  archiveClient: client => dispatch(clientActions.archiveClient(client)),
  deleteClient: client => dispatch(clientActions.deleteClient(client)),
  loadStudents: () => dispatch(studentActions.loadStudents()),
  mergeClients: (sourceId, targetId, mergeData) => dispatch(clientActions.mergeClients(sourceId, targetId, mergeData)),
});


export default connect(mapStateToProps, mapDispatchToProps)(Client);
